import { Fragment, type ReactNode } from 'react';
import { useTranslation } from '@mypoz/i18n';
import { Icon, cn } from '@mypoz/ui';

import { actionLinkClass } from '@/lib/styles.js';
import { useCopyToClipboard } from '@/lib/clipboard.js';

export interface CopyableFieldProps {
  label: string;
  /** Shown and copied verbatim — the two are the same string by design. */
  value: string;
  /** BCP-47 tag for the value, so a screen reader pronounces it correctly. */
  lang?: string;
  /**
   * Substring to pick out visually wherever it occurs — in practice the customer code.
   *
   * Display only. The clipboard still receives `value` untouched, because copying is programmatic
   * rather than a scrape of the rendered node, so marking the code up costs the copy nothing.
   */
  highlight?: string;
}

/**
 * The customer code is the one part of an address a reader has to verify at a glance, and inside a
 * long Chinese address string it is otherwise invisible. Splitting on a plain substring rather
 * than a pattern keeps this free of regex escaping.
 */
function withHighlight(value: string, highlight?: string): ReactNode {
  if (!highlight) return value;

  const parts = value.split(highlight);
  if (parts.length === 1) return value;

  return parts.map((part, index) => (
    <Fragment key={index}>
      {index > 0 ? (
        <span className="mx-0.5 rounded bg-brand-soft px-1.5 py-0.5 text-xs font-semibold text-brand">
          {highlight}
        </span>
      ) : null}
      {part}
    </Fragment>
  ));
}

/**
 * One labelled row with its own copy button, mirroring the legacy 仓库地址 page.
 *
 * Per-field rather than one copy-everything button because that is what the destination needs:
 * Chinese, Japanese and Korean checkout forms ask for receiver, phone, postcode and street in
 * separate inputs, with the region chosen from cascading dropdowns. A single blob has to be
 * pulled apart by hand on arrival, which is how a postcode ends up in an address line.
 *
 * Each row owns its own copy state. Sharing one flag across a section would flip every button on
 * the card at once, so the reader could not tell which field they actually took.
 */
export function CopyableField({ label, value, lang, highlight }: CopyableFieldProps) {
  const { t } = useTranslation();
  const { copied, failed, copy } = useCopyToClipboard();

  // A blank value would render a labelled row with nothing in it and a button that reports
  // success while wiping whatever the reader had on their clipboard. Not every warehouse carries
  // every field, so drop the row instead.
  if (value.trim() === '') return null;

  const action = failed
    ? t('warehouses.copyFailed')
    : copied
      ? t('warehouses.copied')
      : t('warehouses.copy');

  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-4">
      <span className="shrink-0 text-sm text-muted sm:w-28">{label}</span>

      <span lang={lang} className="min-w-0 flex-1 text-sm break-words text-ink">
        {withHighlight(value, highlight)}
      </span>

      {/*
        The accessible name carries the field, because the visible label is a sibling with no
        programmatic link to the button. Without it this screen presents twenty-four controls all
        called "Copy", and a rotor or a voice command cannot tell the Guangzhou address from the
        Japan phone number.
      */}
      <button
        type="button"
        onClick={() => void copy(value)}
        aria-label={`${action} ${label}`}
        className={actionLinkClass(cn('shrink-0 self-start sm:self-auto', failed && 'text-danger'))}
      >
        {action}
        <Icon name={failed ? 'error' : copied ? 'check' : 'content_copy'} size={16} />
      </button>

      {/* A changed button label is not announced; this is what tells a screen reader the outcome. */}
      <span aria-live="polite" className="sr-only">
        {copied || failed ? `${label} ${action}` : ''}
      </span>
    </div>
  );
}
